import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  FormLabel,
  TextField,
} from "@mui/material";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import EditIcon from "@mui/icons-material/Edit";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { useForm } from "react-hook-form";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import {
  Button as CustomButton,
  ButtonVariantOption,
  ButtonColorOption,
} from "@/components/Button";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import type { AdditionalFieldsFormData } from "@/features/map/types";
import { AdditionalFieldsModal } from "../AdditionalFieldsModal";
import {
  convertStringToAdditionalFields,
  convertFormDataToAdditionalFields,
} from "../../utils/additionalFieldsHelpers";
import { useFeatureFlags } from "@/configs/featureFlag";

interface TagsAndRemarksSectionProps {
  handlePinSave?: () => void;
  getInitialTags?: () => string[];
  onTagsChange?: (tags: string[]) => void;
  getInitialRemarks?: () => string;
  onRemarksChange?: (remarks: string) => void;
  getInitialAdditionalFields?: () => string;
  onAdditionalFieldsChange?: (additionalFields: string) => void;
  availableFields?: AdditionalFieldConfig;
  hasAvailableFields?: boolean;
}

/**
 * タグと備考セクション
 */
export const TagsAndRemarksSection = ({
  handlePinSave,
  getInitialTags,
  onTagsChange,
  getInitialRemarks,
  onRemarksChange,
  getInitialAdditionalFields,
  onAdditionalFieldsChange,
  availableFields,
  hasAvailableFields,
}: TagsAndRemarksSectionProps): JSX.Element => {
  const { crm } = useFeatureFlags();

  // タグ管理
  const { control, getValues, setValue } = useForm<{ tags: string[] }>({
    defaultValues: { tags: getInitialTags?.() ?? [] },
  });
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [displayTags, setDisplayTags] = useState<string[]>(
    getInitialTags?.() ?? []
  );

  // 備考管理
  const [isEditingRemarks, setIsEditingRemarks] = useState(false);
  const [remarksValue, setRemarksValue] = useState<string>(
    getInitialRemarks?.() ?? ""
  );

  // 追加項目管理
  const [additionalFieldsModalOpen, setAdditionalFieldsModalOpen] =
    useState(false);
  const [additionalFieldsValue, setAdditionalFieldsValue] = useState<string>(
    getInitialAdditionalFields?.() ?? ""
  );

  // 初期値の更新
  useEffect(() => {
    if (getInitialTags) {
      const tags = getInitialTags();
      setValue("tags", tags);
      setDisplayTags(tags);
    }
    if (getInitialRemarks) {
      setRemarksValue(getInitialRemarks());
    }
    if (getInitialAdditionalFields) {
      setAdditionalFieldsValue(getInitialAdditionalFields());
    }
  }, [getInitialTags, getInitialRemarks, getInitialAdditionalFields, setValue]);

  const handleTagsChange = (newTags: string[]): void => {
    if (onTagsChange) {
      onTagsChange(newTags);
    }
  };

  const completeTagsEditing = (): void => {
    const currentTags = getValues("tags");
    setDisplayTags(currentTags);
    setIsEditingTags(false);
  };

  const completeRemarksEditing = (): void => {
    if (onRemarksChange) {
      onRemarksChange(remarksValue);
    }
    setIsEditingRemarks(false);
  };

  const handleAdditionalFieldsSave = (
    formData: Record<string, unknown>
  ): void => {
    if (!availableFields) return;
    const convertedData = convertFormDataToAdditionalFields(
      formData as AdditionalFieldsFormData,
      availableFields
    );
    const additionalFieldsString = JSON.stringify(convertedData);
    setAdditionalFieldsValue(additionalFieldsString);
    if (onAdditionalFieldsChange) {
      onAdditionalFieldsChange(additionalFieldsString);
    }
    setAdditionalFieldsModalOpen(false);
  };

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Box component="section" aria-label="タグと備考" sx={{ px: 3 }}>
        {/* タグセクション */}
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mb: 1 }}
          >
            <LocalOfferIcon fontSize="small" />
            <Typography sx={{ fontWeight: 700 }}>タグ</Typography>
          </Stack>
          {!isEditingTags ? (
            <Box>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                sx={{ mb: 1, gap: 0.5 }}
              >
                {displayTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{
                      fontSize: "12px",
                      height: "24px",
                    }}
                  />
                ))}
              </Stack>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => {
                  setIsEditingTags(true);
                }}
                sx={{
                  padding: "4px 16px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  textTransform: "none",
                }}
              >
                +タグを追加
              </Button>
            </Box>
          ) : (
            <Box>
              <CAutocompleteMultiFreeSoloTextField
                control={control}
                name="tags"
                label=""
                size={SizingWrapperStyle.INHERIT}
                callbackOnChange={handleTagsChange}
                maxLength={20}
              />
              <Button
                type="button"
                variant="contained"
                size="small"
                onClick={completeTagsEditing}
                sx={{
                  fontSize: "12px",
                  mt: 1,
                  textTransform: "none",
                }}
              >
                入力完了
              </Button>
            </Box>
          )}
        </Box>

        {/* 備考セクション */}
        <Box sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
            <FormLabel>
              <NoteAltIcon fontSize={"small"} />
              備考
            </FormLabel>
            <IconButton
              size="small"
              onClick={() => {
                setIsEditingRemarks(true);
              }}
              sx={{ ml: 1 }}
              aria-label="編集"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>

          {isEditingRemarks ? (
            <>
              <TextField
                value={remarksValue}
                onChange={(e) => {
                  setRemarksValue(e.target.value);
                }}
                fullWidth
                multiline
                minRows={3}
                maxRows={4}
                variant="outlined"
                sx={{ mt: 1, mb: 1 }}
                inputProps={{ maxLength: 200 }}
                size="small"
              />
              <Grid
                container
                justifyContent="flex-end"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setRemarksValue(getInitialRemarks?.() ?? "");
                      setIsEditingRemarks(false);
                    }}
                  >
                    キャンセル
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={completeRemarksEditing}
                  >
                    入力完了
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography
              ml={2}
              my={1}
              fontSize={"0.8rem"}
              variant={"body1"}
              component={"div"}
              sx={{
                whiteSpace: "pre-wrap",
                minHeight: "1.5rem",
                wordBreak: "break-word",
              }}
            >
              {remarksValue || ""}
            </Typography>
          )}
        </Box>

        {/* 追加項目セクション */}
        {hasAvailableFields && (
          <Box sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center">
              <FormLabel>
                <PlaylistAddIcon fontSize={"small"} />
                カスタム項目
              </FormLabel>
              <Box
                component="span"
                sx={{
                  ml: 1,
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: "0.875rem",
                  "&:hover": {
                    color: "primary.dark",
                  },
                }}
                onClick={() => {
                  setAdditionalFieldsModalOpen(true);
                }}
              >
                編集
              </Box>
            </Box>
          </Box>
        )}

        {/* ピン保存ボタン */}
        {crm && handlePinSave && (
          <Box sx={{ mb: 2, width: "50%" }}>
            <CustomButton
              label={"ピンを保存"}
              onClick={handlePinSave}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Outlined}
              color={ButtonColorOption.Primary}
            />
          </Box>
        )}
      </Box>
      <AdditionalFieldsModal
        open={additionalFieldsModalOpen}
        onClose={() => {
          setAdditionalFieldsModalOpen(false);
        }}
        availableFields={availableFields}
        initialValues={convertStringToAdditionalFields(
          additionalFieldsValue,
          availableFields
        )}
        onSave={handleAdditionalFieldsSave}
      />
    </>
  );
};
